import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertTriangle, Clock, Sparkles, Radio, Camera } from "lucide-react";
import { axiosInstance } from "../lib/axios";
import { money, count, pct } from "../components/reports/ReportTabs";

// ── A shared report ──────────────────────────────────────────────────────────
//
// Its own page, not a redirect into the dashboard. Someone opening this from
// a WhatsApp message on a phone wants the report, not a workspace they may
// not be able to sign into.
//
// Built mobile-first for that reason: cards stack, charts fit the width, and
// nothing needs horizontal scrolling.
//
// The snapshot/live distinction is stated at the top rather than buried,
// because it changes what the figures mean. A sales director forwarding a
// link needs to know whether they are sending a record or a window.

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState<any>(null);
  const [mode, setMode] = useState<string>("snapshot");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needsAuth, setNeedsAuth] = useState(false);

  useEffect(() => {
    let live = true;
    axiosInstance
      .get(`/reports/shared/${token}`)
      .then(({ data }) => {
        if (!live) return;
        setReport(data.report);
        setMode(data.mode);
      })
      .catch((e) => {
        if (!live) return;
        setNeedsAuth(Boolean(e?.response?.data?.requiresAuth));
        setError(e?.response?.data?.message || "This report could not be opened.");
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <Loader2 size={22} className="animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
        <div className="max-w-sm text-center">
          <AlertTriangle size={26} className="mx-auto text-amber-500" />
          <p className="text-[15px] font-bold text-slate-800 mt-2">{error}</p>
          {needsAuth && (
            <a href="/login" className="inline-block mt-3 px-4 py-2 rounded-lg bg-[#2462b2] text-white text-[13px] font-bold">
              Sign in
            </a>
          )}
        </div>
      </div>
    );
  }

  const d = report?.data;
  const summary = report?.summary || d?.summary;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        <div>
          <h1 className="text-[20px] sm:text-[24px] font-extrabold text-slate-900 leading-tight">{report.title}</h1>
          <p className="text-[13px] text-slate-500">{report.periodLabel}</p>

          {/* Stated plainly, because it changes what every number below means. */}
          <div className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
            mode === "live" ? "bg-sky-50 border-sky-200 text-sky-800" : "bg-slate-100 border-slate-200 text-slate-600"
          }`}>
            {mode === "live" ? <><Radio size={10} /> Live — these figures update as the records change</>
                             : <><Camera size={10} /> Snapshot — the figures as they were when this was generated</>}
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
            <Clock size={10} /> Generated {new Date(report.createdAt).toLocaleString()}
            {report.createdByName && ` by ${report.createdByName}`}
          </p>
        </div>

        {!d ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-[13px] text-slate-500">
            This report has no figures stored.
          </div>
        ) : (
          <>
            {summary && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SummaryBlock title="Performance Highlights" items={summary.highlights} tone="emerald" />
                <SummaryBlock title="Attention Required" items={summary.attention} tone="rose" />
                <SummaryBlock title="Forecast Outlook" items={summary.outlook} tone="violet" estimate />
                <SummaryBlock title="Recommended Actions" items={summary.actions} tone="sky" />
              </div>
            )}

            {/* Two per row on a phone rather than one — eight cards in a single
                column is a lot of scrolling for a glance. */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              <Kpi label="Deals won" value={count(d.kpis?.dealsWon)} tone="emerald" />
              <Kpi label="New clients" value={count(d.kpis?.newClients)} />
              <Kpi label="Projects created" value={count(d.kpis?.projectsCreated)} />
              <Kpi label="Revenue collected" value={money(d.kpis?.actualRevenue)} tone="emerald" />
              <Kpi label="Bids" value={count(d.kpis?.bids?.count)} sub={money(d.kpis?.bids?.value)} />
              <Kpi label="Proposals" value={count(d.kpis?.proposals)} />
              <Kpi label="Follow-ups overdue" value={count(d.kpis?.followUps?.overdue)} tone={d.kpis?.followUps?.overdue ? "rose" : "slate"} />
              <Kpi label="Win rate" value={pct(d.pipeline?.winRate)} sub="closed deals only" />
            </div>

            {d.charts?.outcomes && (
              <Card title="Opportunity outcomes">
                <div className="space-y-2">
                  {d.charts.outcomes.map((o: any) => {
                    const total = d.charts.outcomes.reduce((s: number, x: any) => s + x.value, 0) || 1;
                    const colour = o.label === "Won" ? "bg-emerald-500" : o.label === "Lost" ? "bg-rose-500" : "bg-amber-500";
                    return (
                      <div key={o.label}>
                        <div className="flex justify-between text-[12px]">
                          <span className="text-slate-600">{o.label}</span>
                          <span className="font-bold text-slate-800">{count(o.value)}</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                          <div className={`h-full ${colour} rounded-full`} style={{ width: `${(o.value / total) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {d.targets?.length > 0 && (
              <Card title="Target achievement">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {d.targets.map((t: any) => (
                    <div key={t.id} className={`rounded-xl border p-3 ${t.behind ? "border-amber-200 bg-amber-50" : "border-slate-200"}`}>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{t.metricLabel}</p>
                      <p className="text-[17px] font-extrabold text-slate-900">
                        {t.unit === "currency" ? money(t.achieved) : count(t.achieved)}
                        <span className="text-[12px] text-slate-400"> / {t.unit === "currency" ? money(t.target) : count(t.target)}</span>
                      </p>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                        <div className={`h-full rounded-full ${t.behind ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, t.pct ?? 0)}%` }} />
                      </div>
                      <p className="text-[10.5px] text-slate-500 mt-1">{t.periodLabel} · {t.status}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Forecast kept apart from everything above, and labelled. */}
            {d.forecast && (
              <Card title="Forecast" tone="violet">
                {d.forecast.available ? (
                  <>
                    <p className="text-[11px] font-bold uppercase tracking-wide text-violet-600">Estimate</p>
                    <p className="text-[24px] font-extrabold text-violet-700">{money(d.forecast.weightedPipeline)}</p>
                    <ul className="mt-2 space-y-0.5">
                      {(d.forecast.assumptions || []).map((a: string, i: number) => (
                        <li key={i} className="text-[11.5px] text-slate-500 pl-2.5 relative">
                          <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-slate-300" />{a}
                        </li>
                      ))}
                    </ul>
                    <p className="text-[11px] font-semibold text-violet-700 mt-2">Confidence: {d.forecast.confidence}</p>
                  </>
                ) : (
                  <p className="text-[12px] text-slate-500">{d.forecast.reason}</p>
                )}
              </Card>
            )}
          </>
        )}

        <p className="text-[11px] text-slate-400 text-center pt-2">Omni Workspace</p>
      </div>
    </div>
  );
}

const Card = ({ title, tone, children }: any) => (
  <div className={`rounded-2xl border bg-white p-4 ${tone === "violet" ? "border-violet-200" : "border-slate-200"}`}>
    <p className="text-[13px] font-bold text-slate-800 mb-2">{title}</p>
    {children}
  </div>
);

const Kpi = ({ label, value, sub, tone = "slate" }: any) => {
  const tones: Record<string, string> = { slate: "text-slate-800", emerald: "text-emerald-600", rose: "text-rose-600" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 leading-tight">{label}</p>
      <p className={`text-[18px] font-extrabold mt-0.5 ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-[10.5px] text-slate-500">{sub}</p>}
    </div>
  );
};

const SummaryBlock = ({ title, items, tone, estimate }: any) => {
  const tones: Record<string, string> = {
    emerald: "border-emerald-200 bg-emerald-50/50",
    rose: "border-rose-200 bg-rose-50/50",
    violet: "border-violet-200 bg-violet-50/50",
    sky: "border-sky-200 bg-sky-50/50",
  };
  if (!items?.length) return null;
  return (
    <div className={`rounded-2xl border p-3.5 ${tones[tone]}`}>
      <p className="text-[12px] font-bold text-slate-800 flex items-center gap-1.5"><Sparkles size={12} /> {title}</p>
      {estimate && <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600 mt-0.5">Estimate</p>}
      <ul className="mt-1.5 space-y-1">
        {items.map((t: string, i: number) => (
          <li key={i} className="text-[12px] text-slate-700 leading-snug pl-2.5 relative">
            <span className="absolute left-0 top-[7px] w-1 h-1 rounded-full bg-slate-400" />{t}
          </li>
        ))}
      </ul>
    </div>
  );
};
